import React from "react";
import OrgPortalSectionTitle from "./OrgPortalSectionTitle";
import { Divider } from "@mui/material";
import useTranslation from "../../../i18n/useTranslation";
import {formattedDateTime} from "../../../util/date";

export default function OrgPortalLatestResultsSection({ results, theme }) {
    const {
        surfaceColor,
        cardBorder,
        textOnSurface,
        accentColor,
        primaryColor,
        textOnBackground,
    } = theme;
    const {t, language} = useTranslation();

    // Helper: nice date/time string using event timezone if available


    // Helper: competition context (division / season / stage / group)
    const formatContext = (r) => {
        const divisionName = r.season?.division?.name;
        const seasonName = r.season?.name;
        const stageName = r.stage?.name;
        const groupName =
            r.game?.group?.name || (r.stage?.groups && r.stage.groups[0]?.name);

        const parts = [];
        if (divisionName) parts.push(divisionName);
        if (seasonName) parts.push(seasonName);
        if (stageName) parts.push(stageName);
        if (groupName) parts.push(`Group ${groupName}`);

        return parts.join(" • ");
    };

    // Helper: team avatar (logo or initials)
    const TeamAvatar = ({ name, logoUrl, highlight }) => {
        const initials = React.useMemo(() => {
            if (!name) return "?";
            return name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0]?.toUpperCase())
                .join("");
        }, [name]);

        const size = 32;

        if (logoUrl) {
            return (
                <div
                    style={{
                        width: size,
                        height: size,
                        borderRadius: "50%",
                        overflow: "hidden",
                        flexShrink: 0,
                        // 🔹 Highlight winner/draw with accentColor, otherwise primary ring
                        border: `2px solid ${
                            highlight
                                ? (accentColor || textOnBackground)
                                : `${primaryColor || textOnSurface}55`
                        }`,
                        // 🔹 Use surface instead of hard black
                        backgroundColor: surfaceColor,
                    }}
                >
                    <img
                        src={logoUrl}
                        alt={name}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                        }}
                    />
                </div>
            );
        }

        const baseAccent = accentColor || textOnBackground;

        return (
            <div
                style={{
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: `${baseAccent}22`,
                    border: `1px solid ${
                        highlight ? baseAccent : `${baseAccent}66`
                    }`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: baseAccent,
                }}
            >
                {initials}
            </div>
        );
    };

    // Helper: responsibles string
    const formatResponsibles = (responsibles) => {
        if (!responsibles || !responsibles.length) return "";
        // support both [{fullName}] and ["Name"]
        const names = responsibles.map((r) =>
            typeof r === "string" ? r : r.fullName || ""
        );
        const filtered = names.filter(Boolean);
        if (!filtered.length) return "";
        return `Responsibles: ${filtered.join(", ")}`;
    };

    return (
        <section style={{ marginBottom: 50 }}>
            <OrgPortalSectionTitle title={t('home.latestResults.title')} theme={theme} />

            {/* 🔹 Section divider: primaryColor (same as other sections) */}
            <Divider style={{ marginBottom: 20 }} color={primaryColor} />

            {results.length === 0 ? (
                <div
                    style={{
                        backgroundColor: surfaceColor,
                        borderRadius: 10,
                        padding: 16,
                        border: `1px solid ${cardBorder}`,
                        color: textOnSurface,
                        fontSize: 14,
                        opacity: 0.8,
                    }}
                >
                    {t('home.latestResults.noItems')}
                </div>
            ) : (
                results.map((r) => {
                    const game = r.game || {};
                    const isNoPlay = game.noPlay;

                    const homeName = game.homeTeam?.name || game.homeTeamAlias || t('home.game.home');
                    const homeLogo = game.homeTeam?.logoUrl || null;
                    const awayName = game.visitorTeam?.name || game.visitorTeamAlias || t('home.game.visitor');
                    const awayLogo = game.visitorTeam?.logoUrl || null;

                    const scoreHome = game.scoreHome ?? null;
                    const scoreAway = game.scoreVisitor ?? null;

                    const homeWinner =
                        scoreHome != null && scoreAway != null && scoreHome > scoreAway;
                    const awayWinner =
                        scoreHome != null && scoreAway != null && scoreAway > scoreHome;
                    const isDraw =
                        scoreHome != null && scoreAway != null && scoreHome === scoreAway;

                    const dateStr = formattedDateTime(r.date, r.timezone, language);
                    const context = formatContext(r);
                    const fieldName = r.field?.name;
                    const responsiblesText = formatResponsibles(game.responsibles);

                    // 🔹 Status pill: accent vs neutral
                    const statusText = isNoPlay ? t('home.game.noPlay') : t(`home.eventStatus.${r.status.toLowerCase()}`) || t('home.game.final');
                    const statusBg = isNoPlay ? `${textOnBackground}22` : `${accentColor}22`;
                    const statusBorder = isNoPlay ? `${textOnBackground}55` : `${accentColor}55`;
                    const statusColor = isNoPlay ? textOnBackground : accentColor;

                    return (
                        <article
                            key={r.id}
                            style={{
                                // 🔹 Card surface style
                                backgroundColor: surfaceColor,
                                padding: 14,
                                borderRadius: 12,
                                marginBottom: 12,
                                border: `1px solid ${cardBorder}`,
                                color: textOnSurface,
                                fontSize: 14,
                                boxShadow: "0 8px 26px rgba(0,0,0,0.25)",
                            }}
                        >
                            {/* Top row: context + status */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 12,
                                    marginBottom: 8,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 14,
                                        opacity: 0.85,
                                        color: textOnSurface,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontWeight: 900,
                                    }}
                                    title={context || r.title}
                                >
                                    {context || r.title || "League game"}
                                </div>

                                <div
                                    style={{
                                        padding: "4px 10px",
                                        borderRadius: 999,
                                        border: `1px solid ${statusBorder}`,
                                        backgroundColor: statusBg,
                                        color: statusColor,
                                        fontSize: 11,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.4,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {statusText}
                                </div>
                            </div>

                            {/* Middle row: teams + score */}
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 16,
                                    marginBottom: 6,
                                }}
                            >
                                {/* Home */}
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        minWidth: 0,
                                    }}
                                >
                                    <TeamAvatar
                                        name={homeName}
                                        logoUrl={homeLogo}
                                        highlight={homeWinner || isDraw}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                opacity: 0.7,
                                                color: textOnSurface,
                                                marginBottom: 2,
                                            }}
                                        >
                                            {t('home.game.home')}
                                        </div>
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 15,
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                color:
                                                    homeWinner || isDraw
                                                        ? accentColor
                                                        : textOnSurface,
                                            }}
                                        >
                                            {homeName}
                                        </div>
                                    </div>
                                </div>

                                {/* Score block */}
                                <div
                                    style={{
                                        flexShrink: 0,
                                        minWidth: 70,
                                        textAlign: "center",
                                        padding: "6px 10px",
                                        borderRadius: 999,
                                        border: `1px solid ${cardBorder}`,
                                        // 🔹 Light accent/surface background, not full black
                                        backgroundColor: `${accentColor}15`,
                                        color: textOnSurface,
                                    }}
                                >
                                    {scoreHome != null && scoreAway != null ? (
                                        <span
                                            style={{
                                                fontWeight: 700,
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {scoreHome}
                                            <span style={{ opacity: 0.7 }}> - </span>
                                            {scoreAway}
                                        </span>
                                    ) : (
                                        <span style={{ opacity: 0.7, fontSize: 12 }}>
                                            {t('home.game.noScore')}
                                        </span>
                                    )}
                                </div>

                                {/* Away (visitor) */}
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        minWidth: 0,
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <div style={{ minWidth: 0, textAlign: "right" }}>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                opacity: 0.7,
                                                color: textOnSurface,
                                                marginBottom: 2,
                                            }}
                                        >
                                            {t('home.game.visitor')}
                                        </div>
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 15,
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                color:
                                                    awayWinner || isDraw
                                                        ? accentColor
                                                        : textOnSurface,
                                            }}
                                        >
                                            {awayName}
                                        </div>
                                    </div>
                                    <TeamAvatar
                                        name={awayName}
                                        logoUrl={awayLogo}
                                        highlight={awayWinner || isDraw}
                                    />
                                </div>
                            </div>

                            {/* Bottom rows: date/field + responsibles */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    marginTop: 4,
                                    fontSize: 12,
                                    opacity: 0.9,
                                    flexWrap: "wrap",
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    {dateStr && <span>{dateStr}</span>}
                                    {fieldName && (
                                        <span style={{ opacity: 0.85 }}>
                                            {t('home.game.field')}: <strong>{fieldName}</strong>
                                        </span>
                                    )}
                                </div>

                                {responsiblesText && (
                                    <div
                                        style={{
                                            marginLeft: "auto",
                                            fontSize: 11,
                                            opacity: 0.85,
                                            textAlign: "right",
                                            maxWidth: 320,
                                        }}
                                        title={responsiblesText}
                                    >
                                        {responsiblesText}
                                    </div>
                                )}
                            </div>
                        </article>
                    );
                })
            )}
        </section>
    );
}
